import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Car, X } from "lucide-react";

import { api } from "../../lib/api";
import {
  ASSIGNMENT_STATUS_LABEL, READINESS_LABEL, READINESS_TONE, detailMessage, humanize, pesosFromCentavos,
} from "../../lib/driverService";

// Task 5: assignment goes through the driver engine. Candidates are shown
// eligible-first with reasons; hard blocks are visible for diagnosis only and
// cannot be assigned. There is no override.
export default function DriverAssignmentPanel({ bookingId, data, onChanged }) {
  const [showPicker, setShowPicker] = useState(false);
  const [dispatch, setDispatch] = useState(null);
  const [refusal, setRefusal] = useState(null);

  const withDriver = data?.rental_service_type === "with_driver";
  const loadDispatch = useCallback(() => {
    if (!withDriver) return;
    api.get(`/driver-service/bookings/${bookingId}/dispatch`).then((r) => setDispatch(r.data)).catch(() => setDispatch(null));
  }, [bookingId, withDriver]);
  useEffect(() => { loadDispatch(); }, [loadDispatch, data?.assigned_driver_id, data?.driver_assignment_status]);

  if (!withDriver) return null;
  const status = data.driver_assignment_status || "pending";
  const replacement = status === "replacement_required";

  const changed = () => { setShowPicker(false); setRefusal(null); onChanged?.(); loadDispatch(); };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-3" data-testid="driver-assignment-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Car className="w-5 h-5" /> Driver</h2>
        <span data-testid="driver-assignment-status"
              className={`text-xs px-2 py-0.5 rounded-full font-medium border ${replacement ? READINESS_TONE.BLOCKED : status === "pending" ? READINESS_TONE.REVIEW_REQUIRED : READINESS_TONE.READY}`}>
          {ASSIGNMENT_STATUS_LABEL[status] || humanize(status)}
        </span>
      </div>

      {replacement && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="driver-replacement-banner">
          The assigned driver can no longer take this trip ({(data.driver_replacement?.codes || []).map(humanize).join(", ")}).
          Assign another driver. Nothing has been cancelled.
        </div>
      )}

      <div className="text-sm text-slate-700">
        {data.passenger_count ? `${data.passenger_count} passenger(s)` : "Passenger count not recorded"}
        {data.driver_service_price_centavos != null ? ` · Driver service ${pesosFromCentavos(data.driver_service_price_centavos)}`
          : data.driver_service_price ? ` · Driver service ₱${Number(data.driver_service_price).toLocaleString()}` : " · Driver service charge not set"}
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowPicker(true)} data-testid={data.assigned_driver_id ? "driver-reassign-btn" : "driver-assign-btn"}
                className="px-3 py-1.5 rounded-lg btn-brand text-xs font-medium">
          {data.assigned_driver_id ? "Change driver" : "Assign driver"}</button>
        {!data.assigned_driver_id && (
          <button data-testid="driver-auto-assign-btn" className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs"
                  onClick={async () => {
                    try {
                      const r = await api.post(`/driver-service/bookings/${bookingId}/auto-assign`);
                      if (r.data.assigned) { toast.success("A fully ready driver was assigned"); changed(); }
                      else toast.info(r.data.message);
                    } catch (err) { toast.error(detailMessage(err)); }
                  }}>Suggest and assign</button>
        )}
        {data.assigned_driver_id && (
          <button data-testid="driver-unassign-btn" className="px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 text-xs"
                  onClick={async () => {
                    const reason = window.prompt("Reason for removing the driver");
                    if (!reason) return;
                    try { await api.post(`/driver-service/bookings/${bookingId}/unassign`, { reason }); toast.success("Driver removed"); changed(); }
                    catch (err) { toast.error(detailMessage(err)); }
                  }}>Remove driver</button>
        )}
      </div>

      {refusal && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900" data-testid="driver-refusal">
          <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Cannot assign this driver</div>
          <p className="mt-1">{refusal.message}</p>
          <ul className="mt-1 list-disc pl-4">
            {(refusal.reasons || []).filter((r) => r.severity === "BLOCK" || r.severity === "UNAVAILABLE").map((r) =>
              <li key={r.code}>{r.message}{r.next_action ? ` ${r.next_action}` : ""}</li>)}
          </ul>
        </div>
      )}

      {showPicker && <CandidatePicker bookingId={bookingId} onClose={() => setShowPicker(false)}
                                      onRefused={setRefusal} onDone={changed} />}

      {dispatch && <DispatchReadiness result={dispatch} />}

      <p className="text-xs text-slate-400">
        <Link to="/app/driver-service" className="hover:underline">Driver service</Link>: readiness, schedules and licences.
      </p>
    </div>
  );
}

function CandidatePicker({ bookingId, onClose, onRefused, onDone }) {
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get(`/driver-service/bookings/${bookingId}/candidates`, { params: { show_all: showAll } })
      .then((r) => setData(r.data)).catch((err) => toast.error(detailMessage(err)));
  }, [bookingId, showAll]);

  const pick = async (profileId) => {
    setBusy(profileId);
    onRefused(null);
    try {
      await api.post(`/driver-service/bookings/${bookingId}/assign`, { profile_id: profileId, reason: reason || null });
      toast.success("Driver assigned");
      onDone();
    } catch (err) {
      if (err?.response?.status === 409) onRefused(err.response.data.detail);
      else toast.error(detailMessage(err));
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="driver-candidate-picker">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Choose a driver</span>
        <button onClick={onClose} aria-label="Close"><X className="w-4 h-4" /></button>
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason / note (optional)"
             className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
      {!data && <div className="text-xs text-slate-500">Checking drivers…</div>}
      {data && data.eligible.length === 0 && <div className="text-sm text-slate-600" data-testid="driver-no-candidates">No driver can take this trip right now.</div>}
      <ul className="divide-y divide-slate-100">
        {(data?.eligible || []).map((c) => (
          <li key={c.profile_id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`driver-candidate-${c.profile_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{c.display_name}
                {c.profile_id === data.suggested_profile_id && <span className="ml-2 text-[11px] text-emerald-700">Suggested</span>}</div>
              <div className="text-xs text-slate-500">
                {(c.reasons || []).filter((r) => r.severity !== "WARNING").map((r) => humanize(r.code)).join(" · ") || "No issues found"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${READINESS_TONE[c.status]}`}>{READINESS_LABEL[c.status]}</span>
              <button disabled={busy === c.profile_id} onClick={() => pick(c.profile_id)}
                      className="px-3 py-1 rounded-lg border border-slate-300 text-xs disabled:opacity-50">Assign</button>
            </div>
          </li>
        ))}
      </ul>
      {data?.excluded?.length > 0 && (
        <details open={showAll}>
          <summary className="text-xs text-slate-600 cursor-pointer">{data.excluded.length} driver(s) cannot take this trip</summary>
          <ul className="mt-1 text-xs text-slate-600 space-y-1" data-testid="driver-excluded">
            {data.excluded.map((c) => (
              <li key={c.profile_id}>{c.display_name}: {(c.reason_codes || (c.reasons || []).filter((r) => r.severity === "BLOCK" || r.severity === "UNAVAILABLE").map((r) => r.code)).map(humanize).join(", ")}</li>
            ))}
          </ul>
        </details>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show full details for diagnosis
      </label>
      <p className="text-[11px] text-slate-500">{data?.note}</p>
    </div>
  );
}

function DispatchReadiness({ result }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="dispatch-readiness">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Trip dispatch readiness</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${READINESS_TONE[result.status]}`}>{READINESS_LABEL[result.status]}</span>
      </div>
      <ul className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
        {Object.entries(result.gates).map(([name, gate]) => (
          <li key={name} className="rounded border border-slate-100 p-2">
            <div className="flex justify-between gap-2"><span className="font-medium">{humanize(name)}</span>
              <span>{READINESS_LABEL[gate.status]}</span></div>
            {gate.reasons.filter((r) => r.severity !== "INFO").map((r) => <div key={r.code} className="text-slate-600">{r.message}</div>)}
          </li>
        ))}
      </ul>
      {result.status === "REVIEW_REQUIRED" && (
        <p className="mt-2 text-xs text-slate-600">
          {result.acknowledgement_allowed
            ? "A manager with dispatch permission acknowledges these review items when starting the handover."
            : "Your settings require these review items to be resolved before the trip starts."}
        </p>
      )}
    </div>
  );
}
